import { createRouter, createWebHistory } from 'vue-router';
import { updateHead } from '@/utils/head';

const routes = [
  {
    path: '/',
    redirect: '/about', // Redirect to about page
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/About.vue'),
    meta: {
      title: 'Brenden Runion | Full Stack Engineer & Data Scientist',
      description:
        'Brenden Runion is a full stack engineer and data scientist in Denver, Colorado, building data pipelines, ML models, and the applications people use to act on them.',
      ogType: 'profile',
    },
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/Projects.vue'),
    meta: {
      title: 'Projects | Brenden Runion',
      description:
        'Selected projects from Brenden Runion, including a ReAct fact-checking agent evaluated against a RAG baseline and a semantic search engine built over 4.4 million Wikipedia articles.',
      ogType: 'website',
    },
  },
  {
    path: '/resume',
    name: 'resume',
    component: () => import('@/views/Resume.vue'),
    meta: {
      title: 'Resume | Brenden Runion',
      description:
        "Brenden Runion's resume: full stack engineer and data scientist, five years building Wyoming's statewide behavioral health platform, M.S. in Data Science from the University of Denver.",
      ogType: 'website',
    },
  },
  {
    path: '/writing',
    name: 'writing',
    component: () => import('@/views/Writing.vue'),
    meta: {
      title: 'Writing | Brenden Runion',
      description:
        'Writeups on the data science and engineering projects Brenden Runion has built - what worked, what failed, and what it cost.',
      ogType: 'website',
    },
  },
  {
    path: '/writing/:id',
    name: 'writing-detail',
    component: () => import('@/views/WritingDetail.vue'),
    props: true,
    meta: {
      title: 'Writing | Brenden Runion',
      description:
        'Writeups on the data science and engineering projects Brenden Runion has built - what worked, what failed, and what it cost.',
      // WritingDetail.vue overwrites title/description/OG with the specific
      // writeup's own text once it resolves - see its `writing` watcher.
      dynamicHead: true,
    },
  },
];

// Smoke-test page for the backend API wiring, registered only by "serve:local".
// The NODE_ENV half is what webpack statically folds away, so the TestPage chunk
// is never emitted in a production build; the opt-in half keeps the route off a
// host that happens to run the dev server (NODE_ENV=development) in production.
if (
  process.env.NODE_ENV === 'development' &&
  process.env.VUE_APP_ENABLE_TEST_ROUTE === 'true'
) {
  routes.push({
    path: '/test',
    name: 'test',
    component: () => import('@/views/TestPage.vue'),
  });
}

const router = createRouter({
  history: createWebHistory(), // History mode for routing
  routes,
  scrollBehavior() {
    // Always scroll to top
    return { top: 0 };
  },
});

const DEFAULT_TITLE = 'Brenden Runion';
const DEFAULT_DESCRIPTION =
  'Brenden Runion is a full stack engineer and data scientist in Denver, Colorado, building data pipelines, ML models, and the applications people use to act on them.';

router.afterEach((to) => {
  // /writing/:id owns its own head tags (see WritingDetail.vue) since they
  // depend on which writeup loaded, not just the route.
  if (to.meta?.dynamicHead) return;

  updateHead({
    title: to.meta?.title || DEFAULT_TITLE,
    description: to.meta?.description || DEFAULT_DESCRIPTION,
    path: to.path,
    ogType: to.meta?.ogType || 'website',
  });
});

export default router;
